<template>
  <RfBaseCard>
    <Transition mode="out-in">
      <RfBaseSkeleton v-if="props.loading" height="20" dynamic width="100" />
      <span v-else class="rf-stats-card--title text-body text-beige-50">{{ title }} </span>
    </Transition>
    <Transition mode="out-in">
      <RfBaseSkeleton v-if="props.loading" height="32" dynamic />
      <span v-else class="rf-stats-card--value text-heading-0 text-beige-90">{{ value }}</span>
    </Transition>

    <Transition
      v-if="props.timePeriod === METRICS_PERIODS.last_seven_to_fourteen_days.value"
      mode="out-in"
    >
      <RfBaseSkeleton v-if="props.loading" height="24" dynamic width="80" />
      <span
        v-else
        class="rf-stats-card--grow text-action-buttons text-black-2 relative inline-flex items-center gap-2"
      >
        <span
          ref="tooltipAnchorEl"
          class="rf-stats-card--grow-icon bg-beige-0 grid h-8 w-8 shrink-0 place-content-center rounded-full [&_.rf-icon]:h-6! [&_.rf-icon]:w-6!"
          :class="growData.class"
        >
          <component :is="growData.icon" v-if="growData.icon" />
          <span v-else aria-hidden="true" class="text-grey-2 rounded-full">—</span>
        </span>
        <span> {{ `${growData.sign}${Math.abs(grow)}%` }} </span>
        <button
          class="absolute h-full w-full"
          aria-label="Comparison details"
          :aria-describedby="tooltipEl?.tooltipId"
          @focus="() => tooltipEl?.show?.()"
          @mouseenter="() => tooltipEl?.show?.()"
          @blur="() => tooltipEl?.hide?.()"
          @mouseleave="() => tooltipEl?.hide?.()"
        ></button>
        <RfTooltip ref="tooltipEl" class="mr-0 w-fit" :anchor="tooltipAnchorEl">
          <template #tooltip> compared to the {{ timePeriodName }} </template>
        </RfTooltip>
      </span>
    </Transition>

    <span v-if="icon" class="absolute top-5 right-5">
      <Transition mode="out-in">
        <span v-if="props.loading" key="icon">
          <RfBaseSkeleton height="40" width="40" radius="6" />
        </span>
        <span
          v-else
          key="icon-load"
          class="rf-stats-card--icon bg-beige-0 *:fill-grey-2! inline-flex rounded-sm *:m-auto *:h-10! *:w-10!"
        >
          <component :is="icon" />
        </span>
      </Transition>
    </span>
  </RfBaseCard>
</template>

<script setup>
import { computed, ref } from "vue";
import RfTrendIcon from "@/components/icons/RfTrendIcon.vue";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import { METRICS_PERIODS } from "@/utils/constants/MetricsConstants";
import MetricsUtils from "@/utils/MetricsUtils";
import RfBaseCard from "@/components/cards/RfBaseCard.vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";

const tooltipAnchorEl = ref();
const tooltipEl = ref();

const props = defineProps({
  title: { type: String, default: "" },
  value: { type: [String, Number], default: 0 },
  icon: { type: Object, default: null },
  grow: { type: [String, Number], default: 0 },
  timePeriod: { type: String, default: "" },
  loading: { type: Boolean, default: false },
});

const timePeriodName = computed(
  () =>
    props.timePeriod &&
    MetricsUtils.All_Periods.find(el => el.value === props.timePeriod).name.toLowerCase(),
);

const growData = computed(() => {
  if (+props.grow > 0)
    return {
      class: "bg-green-1! *:fill-green-50!",
      icon: RfTrendIcon,
      sign: "+",
    };
  if (+props.grow < 0)
    return {
      class: "bg-vermillion-1! *:fill-vermillion-50! rotate-z-180 rotate-y-180",
      icon: RfTrendIcon,
      sign: "-",
    };
  return { class: "", icon: null, sign: "" };
});
</script>
